import { FC, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'
import { styled } from 'styled-components'

import { selectAuthState } from 'app/store/selectors'
import { SideMenu } from 'app/features/sideMenu'
import { LoadingContainer } from '@tuhi/components'
import {
    setAwsCredentials,
    setMsalCredentials,
    setTuhiUser,
} from 'app/store/slices/authSlice'
import { handleRefreshToken } from 'app/api/auth/repository/authenticationRepository'
import { requireTuhiUser } from 'app/models/domain/user/tuhiUser'
import { retryAudioUpload } from 'app/di/UseCaseContainer'
import { resumePendingUploads } from 'app/services/resumePendingUploads'
import { RouteUrl } from './interface'
import { checkoutSuccessLoadingText } from 'app/features/settings/planManagement/checkoutSuccess'
import { MobileMenuProvider } from 'app/hooks/useMobileMenu'
import { media } from '@tuhi/components/src/theme/breakpoints'
import { MobileSideMenu } from 'app/features/sideMenu/MobileSideMenu'
import { ConsultCacheProvider } from 'app/context/ConsultCacheContext'

const Container = styled.div`
    display: flex;
    flex-direction: row;
`

const SideMenuWrapper = styled.div`
    max-width: 250px;
    flex-shrink: 0;

    ${media.tablet} {
        max-width: 72px;
    }

    ${media.mobile} {
        display: none;
    }
`

const OutletWrapper = styled.div`
    flex-grow: 1;
`

const Loading = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
`

export const AuthenticatedRoute: FC = () => {
    const dispatch = useDispatch()
    const { pathname } = useLocation()

    const { isAuthenticated, msalCredentials, awsCredentials } =
        useSelector(selectAuthState)

    const refreshToken = useCallback(
        (forceRefresh: boolean) => {
            const refreshToken = async () => {
                const authResponse = await handleRefreshToken(forceRefresh)

                dispatch(setMsalCredentials(authResponse.msalCredentials))
                dispatch(setTuhiUser(requireTuhiUser(authResponse).toData()))
                dispatch(setAwsCredentials(authResponse.awsCredentials))
            }

            refreshToken()
        },
        [dispatch]
    )

    const createRefreshTokenTimer = useCallback(() => {
        if (!msalCredentials || !awsCredentials) return

        // Buffers: MSAL 2 minutes, AWS 5 minutes
        const msalBuffer = 2 * 60 * 1000
        const awsBuffer = 5 * 60 * 1000

        // Convert expiration to Date if it's a string
        const msalExpirationDate =
            typeof msalCredentials.expiration === 'string'
                ? new Date(msalCredentials.expiration)
                : msalCredentials.expiration
        const awsExpirationDate =
            typeof awsCredentials.expiration === 'string'
                ? new Date(awsCredentials.expiration)
                : awsCredentials.expiration

        // Missing expiry used to throw a TypeError on `.getTime()`; keep
        // throwing at the same point, with a clear message.
        if (!awsExpirationDate) {
            throw new Error('AWS credentials have no expiration')
        }

        const msalExpiry = msalExpirationDate.getTime() - msalBuffer
        const awsExpiry = awsExpirationDate.getTime() - awsBuffer

        const triggerTime = Math.min(msalExpiry, awsExpiry)
        const timeoutDelay = triggerTime - Date.now()

        const forceRefresh = awsExpiry < msalExpiry

        if (timeoutDelay > 0) {
            return setTimeout(() => {
                refreshToken(forceRefresh)
            }, timeoutDelay)
        } else {
            refreshToken(forceRefresh)
        }
    }, [msalCredentials, awsCredentials, refreshToken])

    const loadingText = () => {
        if (RouteUrl.CheckoutSuccess.match(pathname)) {
            return checkoutSuccessLoadingText
        }

        return 'Loading...'
    }

    useEffect(() => {
        if (isAuthenticated && msalCredentials && awsCredentials) {
            createRefreshTokenTimer()
        }
    }, [
        isAuthenticated,
        msalCredentials,
        awsCredentials,
        createRefreshTokenTimer,
        refreshToken,
    ])

    // ACA-79: once credentials are available (S3 uploads need them), re-drive any
    // consult audio uploads that were persisted but never completed. Best-effort
    // and idempotent (guarded once-per-session inside resumePendingUploads).
    const hasResumedRef = useRef(false)
    useEffect(() => {
        if (hasResumedRef.current) return
        if (!isAuthenticated || !awsCredentials) return
        hasResumedRef.current = true
        void resumePendingUploads((consultId, audioId, blob) =>
            retryAudioUpload(consultId, audioId, blob, () => {})
        )
    }, [isAuthenticated, awsCredentials])

    const loading = (
        <Loading>
            <LoadingContainer text={loadingText()} isLoading={true} />
        </Loading>
    )

    return (
        <MobileMenuProvider>
            <Container>
                <SideMenuWrapper>
                    <SideMenu />
                </SideMenuWrapper>
                <MobileSideMenu />
                <OutletWrapper>
                    {isAuthenticated ? (
                        <ConsultCacheProvider>
                            <Outlet />
                        </ConsultCacheProvider>
                    ) : (
                        loading
                    )}
                </OutletWrapper>
            </Container>
        </MobileMenuProvider>
    )
}
